// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Plus, Video, Loader2, Upload } from 'lucide-react';
import { VendorReviewManagement } from '@/components/video/VendorReviewManagement';

interface VideoVendorProfile {
  id: string;
  user_id: string;
  business_name?: string;
  portfolio_videos?: string[];
}

const VendorPortfolio: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [vendorProfile, setVendorProfile] = useState<VideoVendorProfile | null>(null);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchVendorProfile();
  }, [user?.id]);

  const fetchVendorProfile = async () => {
    if (!user?.id) return;

    try {
      const { data, error } = await supabase
        .from('video_vendor_profiles')
        .select('*')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) throw error;
      setVendorProfile(data);
    } catch (error) {
      console.error('Error fetching vendor profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleVideoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !vendorProfile) return;

    setUploading(true);
    try {
      // Upload video to Supabase storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${vendorProfile.id}/${Date.now()}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('portfolio-videos')
        .upload(fileName, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('portfolio-videos')
        .getPublicUrl(fileName);

      // Update profile with new video URL
      const currentVideos = vendorProfile.portfolio_videos || [];
      const updatedVideos = [...currentVideos, publicUrl];

      const { error: updateError } = await supabase
        .from('video_vendor_profiles')
        .update({ portfolio_videos: updatedVideos })
        .eq('id', vendorProfile.id);

      if (updateError) throw updateError;

      // Refresh profile data
      await fetchVendorProfile();
    } catch (error) {
      console.error('Error uploading video:', error);
    } finally {
      setUploading(false);
    }
  };

  const removeVideo = async (videoUrl: string) => {
    if (!vendorProfile) return;

    try {
      const currentVideos = vendorProfile.portfolio_videos || [];
      const updatedVideos = currentVideos.filter(url => url !== videoUrl);

      const { error } = await supabase
        .from('video_vendor_profiles')
        .update({ portfolio_videos: updatedVideos })
        .eq('id', vendorProfile.id);

      if (error) throw error;

      // Refresh profile data
      await fetchVendorProfile();
    } catch (error) {
      console.error('Error removing video:', error);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex items-center gap-2">
            <Loader2 className="h-6 w-6 animate-spin" />
            <span>Loading portfolio...</span>
          </div>
        </div>
      </div>
    );
  }

  const portfolioVideos = vendorProfile?.portfolio_videos || [];

  return (
    <div className="p-6 space-y-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Video Portfolio</h1>
          <p className="text-muted-foreground">Showcase your best video work to attract customers</p>
        </div>
        <div className="relative">
          <Button
            disabled={uploading}
            className="flex items-center gap-2"
          >
            {uploading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Upload className="h-4 w-4" />
            )}
            {uploading ? 'Uploading...' : 'Upload Video'}
          </Button>
          <input
            type="file"
            accept="video/*"
            onChange={handleVideoUpload}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            disabled={uploading}
          />
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Portfolio Videos</CardTitle>
        </CardHeader>
        <CardContent>
          {portfolioVideos.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12">
              <Video className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No portfolio videos yet</h3>
              <p className="text-muted-foreground text-center mb-4">
                Upload your best video work to showcase your skills to potential customers
              </p>
            </div>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {portfolioVideos.map((videoUrl, index) => (
                <div key={index} className="relative group">
                  <video
                    src={videoUrl}
                    controls
                    className="w-full aspect-video rounded-lg object-cover"
                  />
                  <Button
                    onClick={() => removeVideo(videoUrl)}
                    variant="destructive"
                    size="sm"
                    className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    Remove
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {vendorProfile && (
        <VendorReviewManagement vendorId={vendorProfile.id} />
      )}
    </div>
  );
};

export default VendorPortfolio;